import { Menu, MenuButton, MenuItem, MenuItems, Portal, Transition } from '@headlessui/react';
import clsx from 'clsx';
import { cloneElement, Fragment, isValidElement, useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import Button from '~/shared/components/ButtonsV2';
import Typography from '~/shared/components/Typography';
import MoreIcon from '~/shared/assets/icons/more.svg';

import type { ActionMenuItem, ActionsMenuProps } from './types';

/** Matches ButtonsV2 `text` variant icon hover (group on parent menu row / trigger). */
export const actionsMenuItemIconClassName =
  'text-gray-600 group-hover:text-primary-500 group-active:text-primary-300';

type ActionsMenuItemIconProps = {
  icon: ReactNode;
  className?: string;
};

export function ActionsMenuItemIcon({ icon, className }: ActionsMenuItemIconProps) {
  if (!isValidElement<{ className?: string }>(icon)) {
    return null;
  }

  return cloneElement(icon, {
    className: clsx(
      'size-5 aspect-square shrink-0',
      actionsMenuItemIconClassName,
      className,
      icon.props.className
    ),
  });
}

const transitionProps = {
  enter: 'transition ease-out duration-100',
  enterFrom: 'transform opacity-0 scale-95',
  enterTo: 'transform opacity-100 scale-100',
  leave: 'transition ease-in duration-75',
  leaveFrom: 'transform opacity-100 scale-100',
  leaveTo: 'transform opacity-0 scale-95',
};

const DEFAULT_MENU_WIDTH = 280;

function useVisibleItems(items: ActionMenuItem[]) {
  return useMemo(() => items.filter((item) => item.visible !== false), [items]);
}

export default function ActionsMenu({
  items,
  'aria-label': ariaLabel,
  disabled = false,
  busy = false,
  menuWidth = DEFAULT_MENU_WIDTH,
  triggerClassName,
}: ActionsMenuProps) {
  const visibleItems = useVisibleItems(items);
  const [internalBusy, setInternalBusy] = useState(false);
  const isBusy = busy || internalBusy;
  const isTriggerDisabled = disabled || isBusy;

  const handleItemClick = useCallback(
    async (item: ActionMenuItem, close: () => void) => {
      if (item.disabled || isBusy) {
        return;
      }

      setInternalBusy(true);
      try {
        close();
        await item.onClick();
      } catch {
        // Caller or mutation hooks handle errors.
      } finally {
        setInternalBusy(false);
      }
    },
    [isBusy]
  );

  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <Menu as="div" className="relative">
      {({ open, close }) => (
        <>
          <MenuButton
            as={Button}
            variant="text"
            size="sm"
            disabled={isTriggerDisabled}
            className={clsx('w-7 !h-7 aspect-square', triggerClassName)}
            icon={<MoreIcon className="size-5" />}
            aria-label={ariaLabel}
            aria-expanded={open}
            onClick={(event: React.MouseEvent) => {
              event.stopPropagation();
            }}
          />
          <Transition as={Fragment} {...transitionProps}>
            <Portal>
              <MenuItems
                anchor={{ to: 'bottom end', gap: 8 }}
                style={{ width: menuWidth }}
                className="rounded-lg bg-white px-3 py-2 border border-gray-200 shadow-[0px_4px_12.7px_0px_#00000012] focus:outline-none z-[9999]"
                onClick={(e) => e.stopPropagation()}
              >
                {visibleItems.map((item) => (
                  <MenuItem
                    key={item.id}
                    as="button"
                    type="button"
                    disabled={!!(item.disabled || isBusy)}
                    onClick={() => void handleItemClick(item, close)}
                    className={clsx(
                      'group flex w-full items-center gap-3 px-2.5 py-3 rounded-lg text-sm cursor-pointer hover:bg-primary-20',
                      'disabled:opacity-50 disabled:cursor-not-allowed data-[focus]:bg-primary-20'
                    )}
                  >
                    {item.icon ? <ActionsMenuItemIcon icon={item.icon} /> : null}
                    <Typography variant="p3-reg" color="text-primary-900" className="text-left">
                      {item.label}
                    </Typography>
                  </MenuItem>
                ))}
              </MenuItems>
            </Portal>
          </Transition>
        </>
      )}
    </Menu>
  );
}
